import { Modal } from "@/components";
import { ResourceName } from "@/utils";
import { Close, ExpandMore, Search } from "@mui/icons-material";
import {
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
} from "@mui/material";
import { useIsFetching } from "@tanstack/react-query";
import { FC, useState } from "react";
import { TopToolbar as RaTopToolbar, useListContext } from "react-admin";
import { EventsFilter } from "../../modals";
import { FiltersList } from "./filters-list.component";
import { useDebouncedListSearch } from "@/hooks";

type SortField = "type" | "participantsCount" | "createdAt";

const sortFieldToColName = (field: SortField) => {
  switch (field) {
    case "type":
      return "Type";
    case "participantsCount":
      return "Participants count";
    case "createdAt":
      return "Created time";
  }
};

const TopToolbar: FC = () => {
  const { sort, setSort } = useListContext();

  const isUsersFetching = useIsFetching({
    queryKey: [ResourceName.EVENTS, "getList"],
  });

  const [isFiltersModalOpened, setIsFiltersModalOpened] = useState(false);
  const [searchQuery, setSearchQuery] = useDebouncedListSearch();

  return (
    <>
      {/* Modal used here due to RA listContext */}
      <Modal
        title="Filters"
        isOpened={isFiltersModalOpened}
        onClose={() => setIsFiltersModalOpened(false)}
        children={
          <EventsFilter onModalClose={() => setIsFiltersModalOpened(false)} />
        }
      />

      <RaTopToolbar
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          marginTop: 1,
          paddingRight: 0,
        }}
      >
        <Stack
          direction={"row"}
          sx={{ width: "100%", justifyContent: "space-between" }}
        >
          {sort.field ? (
            <Chip
              size="small"
              variant="filled"
              label={`Sorted by: ${sortFieldToColName(
                sort.field as SortField
              )}`}
              onDelete={() => setSort({ field: "createdAt", order: "DESC" })}
            />
          ) : (
            <div />
          )}

          <Stack direction={"row"} spacing={1}>
            <TextField
              size="small"
              variant="outlined"
              placeholder="Search by user"
              sx={{ width: "220px" }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 50 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {!!isUsersFetching && <CircularProgress size={16} />}
                    {!isUsersFetching && !!searchQuery && (
                      <Close
                        sx={{ cursor: "pointer" }}
                        onClick={() => setSearchQuery("")}
                      />
                    )}
                  </InputAdornment>
                ),
              }}
            />
            <Button
              size="small"
              endIcon={<ExpandMore />}
              sx={{ minWidth: "auto" }}
              onClick={() => setIsFiltersModalOpened(true)}
            >
              Filters
            </Button>
          </Stack>
        </Stack>

        <FiltersList />
      </RaTopToolbar>
    </>
  );
};

export { TopToolbar };
